import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./App.css";
import { Navigate, Outlet, RouterProvider, createBrowserRouter } from "react-router-dom";
import AppLayout from "./AppLayout";

import Landing from "./Components/Landing";
import StateCourseContent from "./Components/StateCourseContent";
import OSHACourseContent from "./Components/OshaContent";
import Basket from "./pages/basket";
import BudgetReport from "./pages/budgetreport";
import TeamExpirationReport from "./pages/teamExpirationReport"
import Login from "./Components/Login";
import Signup from "./Components/Signup";
import TeamMembers from "./Components/TeamMembers";
import MyCourses from "./Components/MyCourses";
import CourseContent from "./Components/CourseContent";
import Orders from "./Components/Orders";
import Success from "./pages/success";
import MyCompany from "./Components/MyCompany";
import CompanyMembers from "./Components/CompanyMembers";
import MyCertificates from "./Components/MyCertificates";
import MyNotifications from "./Components/MyNotifications";
import ITTNotification from "./Components/ITTNotification";
import ITSNotification from "./Components/ITSNotification";
import SimpleNotification from "./Components/SimpleNotification";
import Certificate from "./Components/Certificate";
import EmailConfirmed from "./Components/EmailConfirmed";
import TestEmailTemplate from "./Components/TestEmailTemplate";
import ForgetPassword from "./Components/ForgetPassword";
import ContactUs from "./Components/ContactUs";
import AboutUs from "./Components/AboutUs";
import PrivacyPolicy from "./Components/PrivacyPolicy";
import PasswordReset from "./Components/PasswordReset";
import WrappedCheckout from "./pages/CheckOut";
import { PaymentModeProvider } from "./context/PaymentModeContext";

import firebaseApp from "./firebase";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import LaraCheckUserCourseStatus from "./Components/LaraCheckUserCourseStatus";

axios.defaults.baseURL =
  "https://us-central1-safet1st-95fb6.cloudfunctions.net/api";

const auth = getAuth();

function RequireAllowlist({ user, allowedEmails }) {
  const email = String(user?.email || "").toLowerCase().trim();
  const isAllowed = allowedEmails
    .map(e => e.toLowerCase().trim())
    .includes(email);

  if (!user || !isAllowed) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}

function App() {
  const [user, setUser] = useState(JSON.parse(localStorage.getItem("user")));
  const [, setLaraUser] = useState(JSON.parse(localStorage.getItem("larauser")));
  const [state, setState] = useState(null);
  const [stateName, setStateName] = useState("");
  const [searchKey, setSearchKey] = useState("");
  const [landingKeyReset, setLandingKeyReset] = useState(0);
  const [numberOfNotes, setNumberOfNotes] = useState(0);
  const [reset, setReset] = useState(1);

  useEffect(() => {
    if (localStorage.getItem("user")) {
      setUser(JSON.parse(localStorage.getItem("user")));
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem("larauser")) {
      setLaraUser(JSON.parse(localStorage.getItem("larauser")));
    }
  }, []);

  const reserNumberOfNotes = (userEmail) => {
    const db = firebaseApp.firestore();
    return db
      .collection("Notifications")
      .doc(userEmail)
      .collection("Notes")
      .where("seen", "==", false)
      .onSnapshot((snapshot) => {
        setNumberOfNotes(snapshot.size);
      });
  };

  useEffect(() => {
    let unsubscribeNotes = null;

    const unsubscribeAuth = onAuthStateChanged(auth, (userm) => {
      if (unsubscribeNotes) unsubscribeNotes();
      unsubscribeNotes = userm ? reserNumberOfNotes(userm.email) : null;
    });

    return () => {
      if (unsubscribeNotes) unsubscribeNotes();
      unsubscribeAuth();
    };
  }, []);

  const router = useMemo(() => {
    // build routes based on current user state (same behavior as your conditional block)
    const authedRoutes = user
      ? [
          { path: "myorders", element: <Orders /> },
          { path: "coursecontent", element: <CourseContent /> },
          { path: "checkout", element: <WrappedCheckout /> },
          { path: "teammembers", element: <TeamMembers /> },
          { path: "mycourses", element: <MyCourses /> },
          { path: "success", element: <Success /> },
          { path: "myteam", element: <MyCompany /> },
          { path: "team", element: <CompanyMembers /> },
          { path: "mycertificates", element: <MyCertificates /> },
          { path: "notifications", element: <MyNotifications /> },
          { path: "certificate", element: <Certificate /> },
        ]
      : [];

    return createBrowserRouter(
      [
        {
          path: "/",
          element: (
            <AppLayout
              user={user}
              setUser={setUser}
              state={state}
              setStateName={setStateName}
              setSearchKey={setSearchKey}
              setLandingKeyReset={setLandingKeyReset}
              landingKeyReset={landingKeyReset}
              numberOfNotes={numberOfNotes}
            />
          ),
          children: [
            {
              index: true,
              element: (
                <Landing
                  changeState={setState}
                  useStateName={stateName}
                  passSearchKey={searchKey}
                  keyReset={landingKeyReset}
                  passUser={setUser}
                  userInfo={user}
                />
              ),
            },
            { path: "state-course", element: <StateCourseContent /> },
            { path: "osha-course", element: <OSHACourseContent /> },
            { path: "contactus", element: <ContactUs /> },
            { path: "aboutus", element: <AboutUs /> },
            { path: "privacy", element: <PrivacyPolicy /> },
            { path: "basket", element: <Basket /> },

            { path: "signup", element: <Signup passUser={setUser} /> },
            { path: "login", element: <Login passUser={setUser} /> },
            { path: "forgotpassword", element: <ForgetPassword /> },
            { path: "emailconfirmation", element: <EmailConfirmed /> },
            { path: "testemailtemple", element: <TestEmailTemplate /> },
            { path: "passwordreset", element: <PasswordReset /> },

            ...authedRoutes,

            {
              element: (
                <RequireAllowlist
                  user={user}
                  allowedEmails={[
                    "d.mathieu@especsoftware.com",   // Davis Mathieu
                    "n.king@phoenixenv.com",         // Nate King
                    "d.smith@phoenixenv.com",        // Davis Smith
                    "c.smith@phoenixenv.com",        // Casey Smith
                    "s.halberstadt@phoenixenv.com",  // Shauna Halberstadt
                    "oscar@aosh.net",                // Oscar Milian
                  ]}
                />
              ),
              children: [
                { path: "budgetreport", element: <BudgetReport userInfo={user}/>}
              ]
            },
            {
              element: (
                <RequireAllowlist
                  user={user}
                  allowedEmails={[
                    "d.mathieu@especsoftware.com",
                    "d.smith@phoenixenv.com",
                  ]}
                />
              ),
              children: [
                {
                  path: "teamExpirationReport",
                  element: <TeamExpirationReport userInfo={user} />,
                },
              ],
            },

            { path: "teaminvitenotification", element: <ITTNotification /> },
            { path: "shareinvitenotification", element: <ITSNotification /> },
            { path: "simplenotification", element: <SimpleNotification /> },

            {
              path: "laracheckstatus",
              element: (
                <LaraCheckUserCourseStatus
                  changeReset={setReset}
                  propReset={reset}
                  changeLaraUser={setLaraUser}
                />
              ),
            },
          ],
        },
      ],
      {
        future: {
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        },
      }
    );
  }, [
    user,
    state,
    stateName,
    searchKey,
    landingKeyReset,
    numberOfNotes,
    reset,
  ]);

  return (
    <PaymentModeProvider>
      <RouterProvider router={router} />
    </PaymentModeProvider>
  );
}

export default App;
